// Durable autosave for the in-progress NEW-lead capture form.
//
// The capture form and `useCaptureDraft` are in-memory only, so an OS webview
// reclaim (common while the native camera is foregrounded) or an accidental
// reload wipes an unsaved capture with no recovery. This module debounces a
// snapshot of that form into Dexie (`captureAutosave`) so the form can rehydrate
// on its next mount. It is a RECOVERY backstop, not the outbox — a capture is
// only truly durable once enqueued via `enqueueLeadCreate`.
//
// Card bytes are stored as ArrayBuffer, never Blob (coding-standards.md).
import { CAPTURE_AUTOSAVE_NEW_KEY, db, type CaptureAutosaveRow } from '@/lib/db';

const DEBOUNCE_MS = 400;

let pending: ReturnType<typeof setTimeout> | null = null;

/** Debounced write of the current capture snapshot. Cheap to call on every edit. */
export function scheduleCaptureAutosave(snapshot: Omit<CaptureAutosaveRow, 'key' | 'updatedAt'>): void {
  if (pending) clearTimeout(pending);
  pending = setTimeout(() => {
    pending = null;
    void db.captureAutosave.put({ ...snapshot, key: CAPTURE_AUTOSAVE_NEW_KEY, updatedAt: Date.now() });
  }, DEBOUNCE_MS);
}

/** The last autosaved new-lead capture, or undefined when there is none. */
export async function loadCaptureAutosave(): Promise<CaptureAutosaveRow | undefined> {
  return db.captureAutosave.get(CAPTURE_AUTOSAVE_NEW_KEY);
}

/** Drop the autosave — on a successful save or an explicit discard. Cancels any pending debounced write. */
export async function clearCaptureAutosave(): Promise<void> {
  if (pending) {
    clearTimeout(pending);
    pending = null;
  }
  await db.captureAutosave.delete(CAPTURE_AUTOSAVE_NEW_KEY);
}
